import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  schema?: object;
}

export const useSEO = ({ title, description, keywords, schema }: SEOProps) => {
  const location = useLocation();

  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string) => {
      let element = document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updatePropertyTag = (property: string, content: string) => {
      let element = document.querySelector(`meta[property="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
      updatePropertyTag('og:description', description);
      updatePropertyTag('twitter:description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    if (title) {
      updatePropertyTag('og:title', title);
      updatePropertyTag('twitter:title', title);
    }

    // Update canonical URL
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}${location.pathname}`);

    // Update OG URL
    updatePropertyTag('og:url', `${siteUrl}${location.pathname}`);

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Add Schema.org JSON-LD
    if (schema) {
      const schemaId = 'schema-jsonld';
      let schemaScript = document.getElementById(schemaId);
      
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.id = schemaId;
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }

      const schemaWithUrl = {
        '@context': 'https://schema.org',
        ...schema,
        url: `${siteUrl}${location.pathname}`
      };

      schemaScript.textContent = JSON.stringify(schemaWithUrl);
    }

    return () => {
      // Cleanup schema on unmount
      const schemaScript = document.getElementById('schema-jsonld');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [title, description, keywords, schema, location.pathname]);
};
